import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import WebService from "../../Services/WebService";
import "./Register.css";

interface OptionType {
  id: number;
  name: string;
}
interface ErrorState {
  [key: string]: string;
}

const Register: React.FC = () => {
  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [landmark, setLandmark] = useState("");
  const [countries, setCountries] = useState<OptionType[]>([]);
  const [states, setStates] = useState<OptionType[]>([]);
  const [cities, setCities] = useState<OptionType[]>([]);
  const [countryId, setCountryId] = useState<number | "">("");
  const [stateId, setStateId] = useState<number | "">("");
  const [cityId, setCityId] = useState<number | "">("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errors, setErrors] = useState<ErrorState>({});
  const [hasLoadedCountries, setHasLoadedCountries] = useState(false);

  const navigate = useNavigate();

 
  const loadCountries = () => {
    if (hasLoadedCountries) return;
    setLoading(true);
    WebService.getAPI<any>({
      action: "/v1/door-management/users/countries",
    })
      .then((res) => {
        const rawList: any[] =
          Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
        const mapped: OptionType[] = rawList.map((c: any) => ({
          id: c.id ?? c.country_id ?? 0,
          name: c.name ?? c.country_name ?? "",
        }));
        setCountries(mapped);
        setHasLoadedCountries(true);
      })
      .catch(() => {
        setCountries([]);
      })
      .finally(() => setLoading(false));
  };

  
  useEffect(() => {
    if (countryId === "") {
      setStates([]);
      setStateId("");
      setCities([]);
      setCityId("");
      return;
    }
    setLoading(true);
    WebService.getAPI<any>({
      action: `/v1/door-management/users/countries/${countryId}/states`,
    })
      .then((res) => {
        const rawList: any[] =
          Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
        const mapped: OptionType[] = rawList.map((s: any) => ({
          id: s.id ?? s.state_id ?? 0,
          name: s.name ?? s.state_name ?? "",
        }));
        setStates(mapped);
        setStateId("");
        setCities([]);
        setCityId("");
      })
      .catch(() => {
        setStates([]);
        setStateId("");
        setCities([]);
        setCityId("");
      })
      .finally(() => setLoading(false));
  }, [countryId]);

 
  useEffect(() => {
    if (stateId === "") {
      setCities([]);
      setCityId("");
      return;
    }
    setLoading(true);
    WebService.getAPI<any>({
      action: `/v1/door-management/users/states/${stateId}/cities`,
    })
      .then((res) => {
        const rawList: any[] =
          Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
        const mapped: OptionType[] = rawList.map((c: any) => ({
          id: c.id ?? c.city_id ?? 0,
          name: c.name ?? c.city_name ?? "",
        }));
        setCities(mapped);
        setCityId("");
      })
      .catch(() => {
        setCities([]);
        setCityId("");
      })
      .finally(() => setLoading(false));
  }, [stateId]);


  const validateFields = () => {
    const newErrors: ErrorState = {};
    if (!fullName.trim()) newErrors.fullName = "Name is required.";
    if (!address.trim()) newErrors.address = "Address is required.";
    if (!landmark.trim()) newErrors.landmark = "Landmark is required.";
    if (countryId === "") newErrors.countryId = "Country is required.";
    if (stateId === "") newErrors.stateId = "State is required.";
    if (cityId === "") newErrors.cityId = "City is required.";
    if (!password) newErrors.password = "Password is required.";
    else if (password.length < 6)
      newErrors.password = "Password must be at least 6 characters.";
    if (!confirmPassword)
      newErrors.confirmPassword = "Please confirm your password.";
    else if (confirmPassword !== password)
      newErrors.confirmPassword = "Passwords do not match.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg("");
    if (!validateFields()) return;
    setLoading(true);

    const payload = {
      username: fullName,
      password: password,
      address: {
        address: address,
        landmark: landmark,
        country: countryId as number,
        state: stateId as number,
        city: cityId as number,
      },
    };

    WebService.postAPI<any>({
      action: "/v1/door-management/users",
      body: payload,
    })
      .then((res) => {
        if (res.status_code === 201) {
          setSuccessMsg("🎉 Registration successful! Welcome to Kunal DOOR and More.");
          setTimeout(() => {
            setLoading(false);
            navigate("/login");
          }, 2000);
        } else {
          setErrors({ global: res.message || "Registration failed!" });
          setLoading(false);
        }
      })
      .catch((err) => {
        setErrors({
          global:
            typeof err === "string" ? err : err?.message || "Network error.",
        });
        setLoading(false);
      });
  };

  return (
    <div
      className="register-bg"
      style={{
        minHeight: "100vh",
        width: "100vw",
        background: `url(${process.env.PUBLIC_URL}/login.png) no-repeat center center/cover`,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
      }}
    >
      <div className="register-form-pane">
        <h2 className="register-heading">
          Register to DoorManagement Portal
        </h2>

        {successMsg && (
          <div
            style={{
              background: "#fffbe7",
              border: "1.2px solid #FFD400",
              color: "#1c790b",
              fontWeight: 600,
              fontSize: 16,
              borderRadius: 9,
              padding: "13px 18px",
              marginBottom: 18,
              textAlign: "center",
            }}
          >
            {successMsg}
          </div>
        )}

        {errors.global && (
          <div
            style={{
              color: "#e43e2d",
              fontSize: 14,
              marginBottom: 10,
              textAlign: "center",
            }}
          >
            {errors.global}
          </div>
        )}

        <form
          className="register-form"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          <div className="register-input-group">
            <input
              className="register-input"
              type="text"
              placeholder="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              style={
                errors.fullName
                  ? { borderColor: "#e43e2d", background: "#fff1f0" }
                  : {}
              }
            />
            {errors.fullName && (
              <span style={{ color: "#e43e2d", fontSize: 13, marginLeft: 2 }}>
                {errors.fullName}
              </span>
            )}
          </div>

          <div className="register-input-group">
            <input
              className="register-input"
              type="text"
              placeholder="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              style={
                errors.address
                  ? { borderColor: "#e43e2d", background: "#fff1f0" }
                  : {}
              }
            />
            {errors.address && (
              <span style={{ color: "#e43e2d", fontSize: 13, marginLeft: 2 }}>
                {errors.address}
              </span>
            )}
          </div>

          <div className="register-input-group">
            <input
              className="register-input"
              type="text"
              placeholder="Landmark"
              value={landmark}
              onChange={(e) => setLandmark(e.target.value)}
              style={
                errors.landmark
                  ? { borderColor: "#e43e2d", background: "#fff1f0" }
                  : {}
              }
            />
            {errors.landmark && (
              <span style={{ color: "#e43e2d", fontSize: 13, marginLeft: 2 }}>
                {errors.landmark}
              </span>
            )}
          </div>

          <div className="register-input-group">
            <select
              className="register-input"
              value={countryId}
              onChange={(e) => {
                const val = e.target.value;
                setCountryId(val === "" ? "" : Number(val));
              }}
              onClick={loadCountries}
              style={
                errors.countryId
                  ? { borderColor: "#e43e2d", background: "#fff1f0" }
                  : {}
              }
            >
              <option value="">Select Country</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {errors.countryId && (
              <span style={{ color: "#e43e2d", fontSize: 13, marginLeft: 2 }}>
                {errors.countryId}
              </span>
            )}
          </div>

          <div className="register-input-group">
            <select
              className="register-input"
              value={stateId}
              onChange={(e) => {
                const val = e.target.value;
                setStateId(val === "" ? "" : Number(val));
              }}
              style={
                errors.stateId
                  ? { borderColor: "#e43e2d", background: "#fff1f0" }
                  : {}
              }
              disabled={!states.length}
            >
              <option value="">Select State</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {errors.stateId && (
              <span style={{ color: "#e43e2d", fontSize: 13, marginLeft: 2 }}>
                {errors.stateId}
              </span>
            )}
          </div>

          <div className="register-input-group">
            <select
              className="register-input"
              value={cityId}
              onChange={(e) => {
                const val = e.target.value;
                setCityId(val === "" ? "" : Number(val));
              }}
              style={
                errors.cityId
                  ? { borderColor: "#e43e2d", background: "#fff1f0" }
                  : {}
              }
              disabled={!cities.length}
            >
              <option value="">Select City</option>
              {cities.map((ci) => (
                <option key={ci.id} value={ci.id}>
                  {ci.name}
                </option>
              ))}
            </select>
            {errors.cityId && (
              <span style={{ color: "#e43e2d", fontSize: 13, marginLeft: 2 }}>
                {errors.cityId}
              </span>
            )}
          </div>

          <div className="register-input-group">
            <input
              className="register-input"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={
                errors.password
                  ? { borderColor: "#e43e2d", background: "#fff1f0" }
                  : {}
              }
              minLength={6}
            />
            {errors.password && (
              <span style={{ color: "#e43e2d", fontSize: 13, marginLeft: 2 }}>
                {errors.password}
              </span>
            )}
          </div>

          <div className="register-input-group">
            <input
              className="register-input"
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={
                errors.confirmPassword
                  ? { borderColor: "#e43e2d", background: "#fff1f0" }
                  : {}
              }
              minLength={6}
            />
            {errors.confirmPassword && (
              <span style={{ color: "#e43e2d", fontSize: 13, marginLeft: 2 }}>
                {errors.confirmPassword}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="register-btn"
            disabled={loading || !!successMsg}
            style={{ marginTop: 10 }}
          >
            {loading ? "Registering..." : "Register"}
          </button>
        </form>

        <button
          type="button"
          onClick={() => navigate("/login")}
          style={{
            color: "#FFD400",
            background: "none",
            border: "none",
            textDecoration: "underline",
            fontWeight: 600,
            fontSize: 15,
            cursor: "pointer",
            marginTop: 16,
            marginLeft: 2,
          }}
        >
          Back to Login
        </button>
      </div>
    </div>
  );
};

export default Register;
